<template>
  <div v-if="loading" class="alert alert-info">Loading premiums...</div>
  <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
  <div v-else class="table-responsive">
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>ID</th>
          <th>Category</th>
          <th>Premium Type</th>
          <th>Value (€/MWh)</th>
          <th>Date</th>
          <th>Active</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="filteredPremiums.length === 0">
          <td colspan="8" class="text-center">No premium history found.</td>
        </tr>
        <tr v-for="premium in filteredPremiums" :key="premium.id">
          <td>{{ premium.id }}</td>
          <td>{{ premium.category }}</td>
          <td>{{ premium.premium_type }}</td>
          <td>{{ premium.premium_value }}</td>
          <td>{{ formatPricingDate(premium.date) }}</td>
          <td>
            <span
              :class="
                premium.is_active ? 'badge bg-success' : 'badge bg-secondary'
              "
            >
              {{ premium.is_active ? "Active" : "Inactive" }}
            </span>
          </td>
          <td>{{ formatPricingDateTime(premium.created_at) }}</td>
          <td>
            <button
              class="btn btn-sm btn-danger"
              @click="handleDelete(premium.id)"
              :disabled="deletingIds.has(premium.id)"
              title="Delete premium"
            >
              <span
                v-if="deletingIds.has(premium.id)"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              <i v-else class="fas fa-trash me-1"></i>
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { EacPremium } from "@/market_insights/admin/eacPricingTypes";
import {
  formatPricingDate,
  formatPricingDateTime,
} from "@/market_insights/admin/eacPricingFormat";

defineProps<{
  filteredPremiums: EacPremium[];
  loading: boolean;
  error: string | null;
  deletingIds: Set<number>;
}>();

const emit = defineEmits<{
  "premium-deleted": [];
  "delete-start": [id: number];
  "delete-end": [id: number];
}>();

// Global handlers
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

/**
 * Handle deletion of a premium entry
 *
 * @param premiumId - ID of the premium to delete
 */
const handleDelete = async (premiumId: number): Promise<void> => {
  if (!confirm("Are you sure you want to delete this premium entry?")) {
    return;
  }

  emit("delete-start", premiumId);
  try {
    await goSolidApi.delete(`/api/admin/eac-pricing/premiums/${premiumId}`);
    globalSuccessHandler("Success", "EAC premium deleted successfully");
    emit("premium-deleted");
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    emit("delete-end", premiumId);
  }
};
</script>

<style lang="scss" scoped>
.table-responsive {
  max-height: 70vh;
}
</style>
